import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth, { Authenticator } from '@okta/okta-auth-js';
import { IdxForm } from '@okta/okta-auth-js/types/lib/idx/types/idx-js';
import { asFormControl } from 'src/app/core/utils/common';

@Component({
  selector: 'app-send-code-form',
  templateUrl: './send-code-form.component.html',
  styleUrls: ['./send-code-form.component.scss'],
})
export class SendCodeFormComponent implements OnInit {
  @Input() username: string = '';
  @Input() factor: 'email' | 'phone' = 'email';
  @Input() updateUsername!: (username: string) => void;
  @Input() setPhoneNumber!: (phoneNumber: string) => void;
  @Input() onOtpSent!: (factor: 'email' | 'phone') => void;
  @Input() forgotPassword!: () => void;
  @Input() onUsePassword!: () => void;

  sendCodeForm = new FormGroup({
    username: new FormControl('', [Validators.required, Validators.email]),
    factor: new FormControl('', [Validators.required]),
  });
  disableButton: boolean = false;
  errorMessage: string = '';

  asFormControl = asFormControl;

  constructor(@Inject(OKTA_AUTH) private oktaAuth: OktaAuth, private store: Store) {}

  ngOnInit(): void {
    this.sendCodeForm.setValue({
      username: this.username,
      factor: this.factor,
    });

    this.sendCodeForm.valueChanges.subscribe((value) => {
      this.updateUsername(value.username);
    });
  }

  async onSubmit(): Promise<void> {
    if (this.sendCodeForm.invalid) {
      this.sendCodeForm.markAllAsTouched();
      return;
    }

    this.disableButton = true;
    this.errorMessage = '';

    try {
      await this.oktaAuth.idx.clearTransactionMeta();

      const factorResponse = await this.oktaAuth.idx.authenticate({
        username: this.sendCodeForm.value.username,
      });
      const selectAuthenticator = factorResponse.neededToProceed.find(
        (item) => item.name === 'select-authenticator-authenticate'
      );
      const authenticators = selectAuthenticator?.value?.find((item) => item.name === 'authenticator');
      const factor = authenticators?.options?.find(
        (item) => item.label.toLowerCase() === this.sendCodeForm.value.factor.toLowerCase()
      );

      if (!factor) {
        throw new Error('This option is not available on your account.');
      }
      const id = (factor.value as { form: IdxForm }).form.value.find((item) => item.name === 'id')?.value as string;

      const payload: Authenticator & {
        enrollmentId?: string;
      } = {
        id,
      };

      if (this.sendCodeForm.value.factor === 'phone') {
        payload.enrollmentId = (factor.value as { form: IdxForm }).form.value.find(
          (item) => item.name === 'enrollmentId'
        )?.value as string;

        this.setPhoneNumber((factor.relatesTo?.profile?.['phoneNumber'] || 'N/A') as string);
      }

      // Set type of authentication
      await this.oktaAuth.idx.authenticate({
        authenticator: payload,
      });

      if (this.sendCodeForm.value.factor === 'email') {
        payload.methodType = 'email';
      } else if (this.sendCodeForm.value.factor === 'phone') {
        payload.methodType = 'sms';
      }

      // Send code
      const sendResponse = await this.oktaAuth.idx.authenticate({
        authenticator: payload,
      });

      if (sendResponse.status !== 'PENDING') {
        if (sendResponse.messages?.[0].i18n.key === 'errors.E0000150') {
          throw new Error(sendResponse.messages?.[0].message);
        }

        throw new Error('Something went wrong. Please try again.');
      }

      this.onOtpSent(this.sendCodeForm.value.factor);
    } catch (error: any) {
      this.errorMessage = error.message;
      this.disableButton = false;
    }
  }
}
